import { appConfig } from 'config/app';
import {
  AcceptanceFeeCollectionMethod,
  AlertType,
  DisbursementMethod,
  EmployeeStatus,
  EmployeeType,
  HoldbackType,
  IAcceptancePayment,
  IAcceptancePayout,
  IEmployee,
  IEmployeeSite,
  IHoldbackFixed,
  IHoldbackPercent,
  IPayout,
  ISiteRolePolicy,
  ITipAcceptancePaymentSetPayouts,
  PayoutApprovalFailedCheckStatus,
  SiteRole,
} from 'types';

/**
 * Validates the tip acceptance payout and checks for various error conditions based on the employee's status,
 * site role, role policies, and payout limits.
 *
 * This function checks if the employee is eligible to receive tips based on their role and status,
 * whether the current user is attempting to pay themselves, and whether the payout is within allowed limits.
 *
 * @param {IAcceptancePayout} payout - The payout object containing the payout amount and holdback details.
 * @param {IEmployeeSite} site - The site where the employee is working.
 * @param {IEmployee} employee - The employee for whom the payout is being processed.
 * @param {ISiteRolePolicy} rolePolicy - The policy defining payout rules for different site roles (admin, manager, etc.).
 * @param {string} currentUserId - The ID of the current user making the payout.
 * @param {number} [perTipMax] - An optional per-tip maximum limit. If not provided, no maximum limit is enforced.
 * @returns {string} An error message if any validation fails, otherwise an empty string.
 */
export const checkTipAcceptancePayoutForErrors = (payout: IAcceptancePayout, site: IEmployeeSite, employee: IEmployee, rolePolicy: ISiteRolePolicy, currentUserId: string, perTipMax?: number): string => {
  const employeeCurrentSite = employee.sites.find((employeeSite) => employeeSite.id === site.id);

  if (
    (employee.type === EmployeeType.ADMIN && !rolePolicy.organizationAdmin.payable) ||
    (employee.type !== EmployeeType.ADMIN && ((employeeCurrentSite?.role === SiteRole.MANAGER && !rolePolicy.siteManager.payable) || (employeeCurrentSite?.role === SiteRole.ADMIN && !rolePolicy.siteAdmin.payable))) ||
    employee.status === EmployeeStatus.DISMISSED
  ) {
    return 'Employee Not Eligible For Tips – Please remove this payment in order to proceed.';
  }

  if (
    employee.id === currentUserId &&
    ((employee.type === EmployeeType.ADMIN && !rolePolicy.organizationAdmin.paySelf) ||
      (employee.type !== EmployeeType.ADMIN && ((site?.role === SiteRole.MANAGER && !rolePolicy.siteManager.paySelf) || (site?.role === SiteRole.ADMIN && !rolePolicy.siteAdmin.paySelf))))
  ) {
    return 'Cannot Pay Yourself – Please remove this payment in order to proceed.';
  }

  if (perTipMax < payout.amount - payout.holdbackAmount) {
    return "Over Individual Payout Limit - Please edit this user's gross tip amount.";
  }

  // handle minimum tip amount for the platform of $0.01
  if (payout.amount - payout.holdbackAmount < appConfig.TIP.MIN) {
    return "Under Individual Payout Minimum - Please edit this user's gross tip amount.";
  }

  return '';
};

/**
 * Calculates the net payout amount based on the fee collection method.
 *
 * If the fee collection method is set to "HOLDBACK", the net payout will be
 * calculated by subtracting the fees from the total payout amount. Otherwise,
 * the full payout amount is returned without any deductions.
 *
 * @param {IAcceptancePayment} payout - The acceptance payout object, containing the amount and fees.
 * @returns {number} The net payout amount after deducting fees (if applicable).
 */
export const calculateAcceptancePayoutNet = (payout: IAcceptancePayment): number => (payout.fees?.collectionMethod === AcceptanceFeeCollectionMethod.HOLDBACK ? payout.amount - (payout.fees?.total ?? 0) : payout.amount);

/**
 * Generates an alert message based on the payout approval status and failed checks.
 *
 * @param {IPayout} payout - The payout object containing approval status and alert details.
 * @returns {string} The alert message based on the failed check or the default alert message.
 */
export const payoutStatusAlertMessage = (payout: IPayout): string => {
  if (!payout?.alert) return '';

  if (payout.alert.type === AlertType.APPROVAL && payout.approvalCheck) {
    if (!payout.approvalCheck.failedChecks.length) return payout.alert.message;

    const lastFailedCheck = payout.approvalCheck.failedChecks[payout.approvalCheck.failedChecks.length - 1];
    if (lastFailedCheck) {
      switch (lastFailedCheck.name) {
        case PayoutApprovalFailedCheckStatus.LAST_PAYMENT:
          return 'No recent payments';
        case PayoutApprovalFailedCheckStatus.DAILY_VELOCITY_LIMIT:
          return 'Velocity Limit';
        default:
          return payout.alert.message;
      }
    }
  }

  return payout.alert.message;
};

/**
 * Takes a tip amount and calculates amount that should be deducted based on the current site holdback settings and payee disbursement method
 *
 * @param {DisbursementMethod} disbursementMethod - Payee disbursement method
 * @param {IHoldbackFixed | IHoldbackPercent} siteHoldbackSettings - Current site holdback settings
 * @param {number} tipAmount - Amount to apply holdback
 * @returns {number} Holdback value
 */
export const calculateHoldbackValue = (disbursementMethod: DisbursementMethod, siteHoldbackSettings?: IHoldbackFixed | IHoldbackPercent, tipAmount?: number) => {
  if (disbursementMethod !== DisbursementMethod.INSTANT) return 0;

  if (siteHoldbackSettings?.type === HoldbackType.FIXED) return (siteHoldbackSettings as IHoldbackFixed).amount;

  if (!siteHoldbackSettings || !tipAmount || tipAmount < appConfig.TIP.MIN) return 0;

  const percentHoldback = siteHoldbackSettings as IHoldbackPercent;
  const holdbackPercent = percentHoldback.percent / 100; // Get percent as percent.
  const holdbackValue = Math.floor(tipAmount * holdbackPercent);
  if (percentHoldback.cap && holdbackValue > percentHoldback.cap) return percentHoldback.cap; // Return cap when holdback is larger
  return holdbackValue;
};

/**
 * Takes a payment set and returns it with updated holdback values values based on the current site holdback settings
 *
 * @param {ITipAcceptancePaymentSetPayouts} ps - Payment Set
 * @param {IHoldbackFixed | IHoldbackPercent} siteHoldbacks - Current site holdback settings
 * @returns {ITipAcceptancePaymentSetPayouts} Payment Set with updated holdback values
 */
export const calculatePaymentSetHoldbacks = (ps: ITipAcceptancePaymentSetPayouts, siteHoldbacks: IHoldbackFixed | IHoldbackPercent): ITipAcceptancePaymentSetPayouts => {
  let totalHoldbacks = 0;

  const payouts = ps.payouts.map((payout: IAcceptancePayout) => {
    const site = payout.employee.sites.find((site: IEmployeeSite) => site.id === ps.siteId);
    const holdbackAmount = calculateHoldbackValue(site.disbursementMethod, siteHoldbacks, payout.amount);
    totalHoldbacks += holdbackAmount;
    return { ...payout, holdbackAmount };
  });

  return { ...ps, payouts, totals: { ...ps.totals, holdbacks: totalHoldbacks } };
};
